import { CarLike } from '../types';
import { Booking } from './bookingTiles';
import { columnPosition, startOfDayMs } from './timeScale';

// Only these statuses get a background band (others render nothing).
export type ShadedStatus = 'RELEASE_READY' | 'BLOCKED';
const SHADED: ShadedStatus[] = ['RELEASE_READY', 'BLOCKED'];

export type StatusChange = {
  carId?: string;
  timestamp?: string;
  oldStatus?: string;
  newStatus?: string;
};

export type StatusPeriod = {
  x: number; // px from the body's left edge (column 0)
  w: number;
  status: ShadedStatus;
  // Set ONLY for planned future changes (source B) — the originating booking, so a
  // click on the band can open its drawer. History (A) / live-status (C) bands have
  // no booking and aren't clickable.
  booking?: Booking;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const isShaded = (s?: string): s is ShadedStatus =>
  !!s && SHADED.includes(s as ShadedStatus);

// Per-car status background bands, from THREE sources merged together:
//   (A) Historical status-change history — a period is the gap between two
//       consecutive changes (status = the later change's `oldStatus`). Past segments
//       only; the CURRENT segment comes from (C), which is authoritative.
//   (B) Planned future status changes — active "bookings" flagged `statusChange`
//       carry a `requestedStatus` (BLOCKED / RELEASE_READY) over their from→to span.
//   (C) Current live status (BLOCKED / RELEASE_READY) — from the car's `status` +
//       `statusChangedAt`. An active status has no defined end, so it fills
//       statusChangedAt → the range end (indefinite), EXCEPT where planned changes (B)
//       carve out their own windows (so the current status resumes after each one).
// Only RELEASE_READY / BLOCKED produce a band; everything else is left uncoloured.
// All sources feed the SAME renderer (grey solid / dashed-red pattern).
export const buildStatusPeriodsByCar = (
  statusChanges: StatusChange[],
  statusChangeBookings: Booking[],
  cars: CarLike[],
  days: Date[],
  columnsPerDay: number,
  columnWidth: number
): Map<string, StatusPeriod[]> => {
  const map = new Map<string, StatusPeriod[]>();
  if (!days.length) return map;

  const rangeStartMs = startOfDayMs(days[0].getTime());
  const totalColumns = days.length * columnsPerDay; // exclusive right edge

  // Append one clamped period rect to a car's list (no-op for unshaded statuses or
  // periods that fall entirely outside the rendered range). Positions on the active
  // zoom's column scale.
  const pushPeriod = (
    carId: string,
    fromMs: number,
    toMs: number,
    status?: string,
    booking?: Booking
  ) => {
    if (!isShaded(status)) return;
    const startPos = Math.max(
      0,
      columnPosition(fromMs, rangeStartMs, columnsPerDay)
    );
    const endPos = Math.min(
      totalColumns,
      columnPosition(toMs, rangeStartMs, columnsPerDay)
    );
    if (endPos <= startPos) return;
    const period: StatusPeriod = {
      x: startPos * columnWidth,
      w: (endPos - startPos) * columnWidth,
      status,
      booking,
    };
    const list = map.get(carId);
    if (list) list.push(period);
    else map.set(carId, [period]);
  };

  // (A) Historical status-change history.
  if (statusChanges?.length) {
    const byCar = new Map<string, StatusChange[]>();
    statusChanges.forEach((c) => {
      if (!c.carId || !c.timestamp) return;
      const list = byCar.get(c.carId);
      if (list) list.push(c);
      else byCar.set(c.carId, [c]);
    });

    byCar.forEach((changes, carId) => {
      // Past segments only — each gap [prev change → this change] held this change's
      // `oldStatus`. The current segment is handled by (C) from the live car status.
      for (let i = 0; i < changes.length; i += 1) {
        const fromMs =
          i === 0
            ? rangeStartMs
            : new Date(changes[i - 1].timestamp as string).getTime();
        const toMs = new Date(changes[i].timestamp as string).getTime();
        pushPeriod(carId, fromMs, toMs, changes[i].oldStatus);
      }
    });
  }

  // (B) Planned future status changes (block / release-ready bookings). These ARE
  // bookings — the band carries `b` so a click can open its drawer.
  (statusChangeBookings || []).forEach((b) => {
    if (!b.carId || !b.from || !b.to) return;
    pushPeriod(
      b.carId,
      new Date(b.from).getTime(),
      new Date(b.to).getTime(),
      b.requestedStatus,
      b
    );
  });

  // (C) Cars whose LIVE status is shaded (BLOCKED / RELEASE_READY): the current status
  // fills from `statusChangedAt` to the range end (an active status has no defined end),
  // EXCEPT where planned changes (B) carve out their own windows — so e.g. a planned
  // release-ready on a blocked car shows grey for its window and reverts to red after.
  const rangeEndMs = startOfDayMs(days[days.length - 1].getTime()) + DAY_MS;
  (cars || []).forEach((car) => {
    if (!isShaded(car.status) || !car.statusChangedAt) return;
    const startMs = new Date(car.statusChangedAt).getTime();
    // This car's planned-change windows, clipped to [startMs, rangeEndMs], sorted.
    const windows = (statusChangeBookings || [])
      .filter((b) => b.carId === car.id && b.from && b.to)
      .map((b) => ({
        from: new Date(b.from as string).getTime(),
        to: new Date(b.to as string).getTime(),
      }))
      .filter((w) => w.to > startMs && w.from < rangeEndMs)
      .sort((a, b) => a.from - b.from);
    // Fill the gaps AROUND the planned windows with the current status; the planned
    // windows themselves are drawn by (B) with their own requestedStatus.
    let cursor = startMs;
    windows.forEach((w) => {
      if (w.from > cursor) pushPeriod(car.id, cursor, w.from, car.status);
      cursor = Math.max(cursor, w.to);
    });
    if (cursor < rangeEndMs) pushPeriod(car.id, cursor, rangeEndMs, car.status);
  });

  return map;
};
